import i18n from '@/i18n';
import PrPaginationService from '@/components/widgets/PrPaginacaoService';

// Definição dos Callbacks
const CALLBACKS = {
  beforeSearch: 'beforeSearch',
  applyFilters: 'applyFilters',
  afterSearch: 'afterSearch',
  beforeSave: 'beforeSave',
  beforeLoadResource: 'beforeLoadResource',
  afterLoadResource: 'afterLoadResource',
  afterSave: 'afterSave',
};

// Tipos a ser utilizado no CrudMixin
export const CRUD_TYPE = {
  search: 'SEARCH',
  form: 'FORM',
};

/**
 * Mixin contendo todo o comportamento do genérico de um CRUD
 *
 * Uso:
 *  Adicionar aos arrays de Mixins ex: mixins: [ CrudMixin ]
 *
 *  Implementar o método initCrud e informar as propriedades obrigatórias, ex:
 *
 *  initCrud() {
 *    this.serviceType = ProjectService;
 *    this.routerName = 'project';
 *    this.componentType = 'FORM';
 *  }
 */
export default {
  data() {
    return {
      serviceType: null,
      routerName: null,
      resource: {},
      resources: [],
      paginator: null,
      totalResources: 0,
      service: null,
      componentType: CRUD_TYPE.search,
      searchLoading: false,
      saveLoading: false,
      stopSaveData: false,
      validationErrors: {},
      options: {
        perPage: 7,
        skipPagination: false,
        redirectAfterSave: true,
      },
      orderSync: {},
      noDataAvailable: this.$t('messages.notFound'),
      onSearchInit: true,
      reqSaveConfig: {},
    };
  },
  computed: {
    /**
     * Metodo responsavel por montar o titulo do box da pagina.
     */
    titleBox() {
      if (this.componentType === CRUD_TYPE.form && this.resource.id) {
        return i18n.t('views.titles.update');
      } else if (this.componentType === CRUD_TYPE.form && !this.resource.id) {
        return i18n.t('views.titles.register');
      }
      return i18n.t('views.titles.list');
    },
  },
  created() {
    this.checkPreRequisites();
    this.paginator = new PrPaginationService(this.search, this.options.perPage);
    this.orderSync.rowsPerPage = this.paginator.perPage;
    // eslint-disable-next-line
    this.service = new this.serviceType();

    if (this.componentType === CRUD_TYPE.form) {
      this.prepareResource();
    }
  },
  watch: {
    /**
     * Metodo responsavel por realizar a paginacao da grid
     */
    orderSync: {
      handler() {
        this.search(this.paginator.currentPage);
      },
      deep: true,
    },
  },
  methods: {
    /**
     * Metodo responsavel por checar os pre requisitos de quem implementa o CrudMixin.
     */
    checkPreRequisites() {
      if (typeof this.initCrud === 'function') {
        this.callTriggerEvent('initCrud');

        if (!this.serviceType) this.$log.error('É preciso injetar o ServiceType no método initCrud()');
        if (!this.routerName) this.$log.error('É preciso injetar o routerName no método initCrud()');
      } else {
        this.$log.error('Implemente o método initCrud como um evento e injete os pré-requisitos');
      }
    },
    /**
     * Método de pesquisa podendo ser paginado ou nao
     * @param {*} page
     */
    search(page = null) {
      if (this.onSearchInit) {
        this.searchLoading = true;
        (this.options.skipPagination) ? this.normalSearch() : this.paginateSearch(page);
      }
      this.onSearchInit = true;
    },

    /**
     * Metodo de pesquisa sem paginacao
     */
    normalSearch() {
      this.callTriggerEvent(CALLBACKS.beforeSearch);

      return this.service.query(this.mergeFiltersAndSort())
        .then(this.baseSearchSuccessBehaviour)
        .finally(this.baseSearchFinallyBehaviour);
    },

    /**
     * Metodo de pesquisa paginada
     * @param {*} page
     */
    paginateSearch(page) {
      this.paginator.currentPage = page || 1;
      const defaultFilters = { perPage: this.paginator.perPage, page: this.paginator.currentPage };

      this.callTriggerEvent(CALLBACKS.beforeSearch);

      return this.service.query(this.mergeFiltersAndSort(defaultFilters)).then((data) => {
        this.paginator.calcNumberOfPages(data.total);

        return data.items;
      })
      .then(this.baseSearchSuccessBehaviour)
      .finally(this.baseSearchFinallyBehaviour);
    },

    /**
     * Metodo chamado apos a pesquisa ser realizada com sucesso.
     * @param {*} items
     */
    baseSearchSuccessBehaviour(items) {
      this.callTriggerEvent(CALLBACKS.afterSearch);
      this.resources = items;
    },

    /**
     * Metodo chamado apos a pesquisa ser finalizada
     */
    baseSearchFinallyBehaviour() {
      this.searchLoading = false;
    },

    /**
     * Metodo responsavel por retornar o id passado pelo param do route.
     */
    getId() {
      return this.$route.params.id;
    },

    /**
     * Metodo responsavel por preparar o Resource.
     */
    prepareResource() {
      const id = this.getId();

      this.callTriggerEvent(CALLBACKS.beforeLoadResource);

      if (id !== 'new') {
        this.service.get(id).then((data) => {
          this.resource = data;
          this.callTriggerEvent(CALLBACKS.afterLoadResource);
        });
      } else {
        this.resource = {};
      }
    },

    /**
     * Metodo responsavel por salvar o resource.
     */
    save() {
      this.saveLoading = true;
      this.callTriggerEvent(CALLBACKS.beforeSave);

      if (!this.stopSaveData) {
        this.service.save(this.resource, this.reqSaveConfig)
          .then((data) => {
            this.resource = data;

            this.$toast.success(i18n.t('messages.saveSuccess'));

            this.callTriggerEvent(CALLBACKS.afterSave);

            if (this.options.redirectAfterSave) {
              this.goToList();
            }
          }).catch((error) => {
            if (error.response && typeof error.response.data === 'object') {
              this.validationErrors = error.response.data;
            } else {
              this.validationErrors = {};
            }
          }).finally(this.baseSaveFinallyBehaviour);
      } else {
        this.baseSaveFinallyBehaviour();
      }
    },

    /**
     * Metodo chamado apos a finalização do save.
     */
    baseSaveFinallyBehaviour() {
      this.saveLoading = false;
    },

    /**
     * Metodo chamado para excluir o resource
     * @param {*} id
     * @param {*} label
     */
    destroy(id, label) {
      this.$dialog.confirm({ title: 'Atenção', content: `Deseja apagar ${label}?` })
        .then((response) => {
          if (response.action === 'ok') {
            this.service.destroy(id).then(() => {
              this.search();
              this.$toast.success(i18n.t('messages.removeSuccess'));
            });
          }
        });
    },

    /**
     * Metodo responsavel por redirecionar para o form.
     * @param {*} id
     */
    goToForm(id) {
      if (id) {
        this.goTo(`${this.routerName}-form`, { id });
      } else {
        this.goTo(`${this.routerName}-form`, { id: '/new' });
      }
    },

    /**
     * Metodo responsavel para redirecionar para a list.
     */
    goToList() {
      if (this.resource.id) {
        this.goTo(`${this.routerName}-list`, { id: this.resource.id });
      } else {
        this.goTo(`${this.routerName}-list`);
      }
    },

    /**
     * Metodo responsavel por redirecionar.
     * @param {*} routerName
     * @param {*} params
     * @param {*} options
     */
    goTo(routerName, params = {}, options = {}) {
      const routerDefinition = Object.assign(options, { name: routerName, params });

      this.$router.push(routerDefinition);
    },

    /**
     * Metodo responsavel por aplicar a ordenacao e os filtros.
     * @param {*} defaultFilters
     */
    mergeFiltersAndSort(defaultFilters = {}) {
      let filters = Object.assign({}, defaultFilters);

      if (this.orderSync.sortBy) {
        Object.assign(filters, {
          orderBy: this.orderSync.sortBy,
          orderType: (this.orderSync.descending) ? 'DESC' : 'ASC',
        });
      }

      // chama o applyFilters
      if (typeof this[CALLBACKS.applyFilters] === 'function') {
        filters = this[CALLBACKS.applyFilters](filters);
      }

      return this.fixFilter(filters);
    },

    /**
     * Metodo responsavel por executar funcoes disparadas em eventos.
     * @param {*} eventName
     * @param {*} args
     */
    callTriggerEvent(eventName, ...args) {
      if (typeof this[eventName] === 'function') {
        this[eventName](args);
      }
    },

    /**
     * Metodo responsavel por retirar propriedades undefined de objetos.
     * @param {*} filters
     */
    fixFilter(filters = {}) {
      const fixFilters = Object.assign({}, filters);

      Object.keys(fixFilters).forEach((key) => {
        if (fixFilters[key] === undefined) {
          delete fixFilters[key];
        }
      });

      return fixFilters;
    },
  },
};




// WEBPACK FOOTER //
// ./src/components/mixins/CrudMixin.js